/**
 * Второй фактор входа и сеансы (ДС-12, НФ-11, v2-common-06).
 * Второй фактор обязателен для людей с деньгами, администраторов, владельцев компаний, команды портала и внешних
 * участников. Способы: приложение-генератор кодов, код в Telegram, одноразовый код восстановления. Вход по ключу
 * (Face ID, Touch ID, Windows Hello) — уже два фактора: устройство и биометрия.
 */
import { z } from 'zod';
import { MeResponse } from './schemas';

export const SECOND_FACTOR_METHODS = ['totp', 'telegram', 'recovery'] as const;
export type SecondFactorMethod = (typeof SECOND_FACTOR_METHODS)[number];
export const SECOND_FACTOR_METHOD_TITLES: Record<SecondFactorMethod, string> = {
  totp: 'Код из приложения',
  telegram: 'Код в Telegram',
  recovery: 'Код восстановления',
};

/** Сколько дней устройство с отметкой «доверять» не спрашивает код */
export const TRUSTED_DEVICE_DAYS = 7;
/** Сколько кодов восстановления выдаётся */
export const RECOVERY_CODES = 10;

/** Пароль верный — нужен код: ответ /auth/login вместо данных входа */
export const SecondFactorChallenge = z.object({
  secondFactor: z.object({
    ticket: z.string(),
    /** Чем можно подтвердить сейчас */
    methods: z.array(z.enum(SECOND_FACTOR_METHODS)),
    /** Второй фактор обязателен, но ещё не подключён: сначала подключить приложение-генератор */
    enroll: z.boolean(),
  }),
});
export type SecondFactorChallenge = z.infer<typeof SecondFactorChallenge>;

export const LoginResponse = z.union([MeResponse, SecondFactorChallenge]);
export type LoginResponse = z.infer<typeof LoginResponse>;

export const SecondFactorCode = z
  .string()
  .trim()
  .min(6)
  .max(20)
  .transform((v) => v.replace(/[\s-]/g, '').toLowerCase());

export const SecondFactorLoginInput = z.object({
  ticket: z.string().min(16).max(200),
  method: z.enum(SECOND_FACTOR_METHODS),
  code: SecondFactorCode,
  /** Не спрашивать код на этом устройстве 7 дней */
  trustDevice: z.boolean().optional(),
});
export type SecondFactorLoginInput = z.infer<typeof SecondFactorLoginInput>;

export const SecondFactorTicketInput = z.object({ ticket: z.string().min(16).max(200) });

/** Подключение приложения-генератора: секрет показывается один раз — строкой и QR-кодом */
export const TotpSetup = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  qrSvg: z.string(),
});
export type TotpSetup = z.infer<typeof TotpSetup>;

/** Вход после подключения: данные входа и коды восстановления (показываются один раз) */
export const SecondFactorLoginResult = MeResponse.extend({
  recoveryCodes: z.array(z.string()).optional(),
});
export type SecondFactorLoginResult = z.infer<typeof SecondFactorLoginResult>;

export const SecondFactorState = z.object({
  /** Обязателен ли второй фактор этому человеку */
  required: z.boolean(),
  totp: z.boolean(),
  /** Telegram привязан и бот настроен: код может прийти туда */
  telegram: z.boolean(),
  recoveryLeft: z.number().int(),
  trustedDevices: z.number().int(),
  /** Секрет приложения можно сохранить: на сервере задан ключ шифрования */
  totpAvailable: z.boolean(),
});
export type SecondFactorState = z.infer<typeof SecondFactorState>;

/** Начать подключение; при замене уже подключённого приложения — с паролем */
export const TotpStartInput = z.object({ password: z.string().max(200).optional() });

export const SecondFactorConfirmInput = z.object({ code: SecondFactorCode });
export const SecondFactorDisableInput = z.object({ password: z.string().min(1).max(200) });

export const RecoveryCodes = z.object({ recoveryCodes: z.array(z.string()) });
export type RecoveryCodes = z.infer<typeof RecoveryCodes>;

/** Сеанс входа в «Профиле» */
export const SessionView = z.object({
  id: z.uuid(),
  current: z.boolean(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  ip: z.string().nullable(),
  device: z.string(),
});
export type SessionView = z.infer<typeof SessionView>;
